import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Store, Wifi, Phone, MapPin, Loader2, Save, Plus, Trash2, ShoppingBag, RefreshCw, Clock, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const StoreSettings = () => {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [shopifyLocations, setShopifyLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  
  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);
  
  const fetchStores = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/stores`, { headers: getAuthHeader() });
      setStores(response.data);
    } catch (error) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader]);
  
  const fetchShopifyLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const response = await axios.get(`${API_URL}/shopify/locations`, { headers: getAuthHeader() });
      if (response.data.locations) {
        setShopifyLocations(response.data.locations);
      }
    } catch (error) {
      // Silently fail - Shopify might not be connected
      console.log('Could not load Shopify locations:', error.message);
      setShopifyLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [API_URL, getAuthHeader]);
  
  useEffect(() => {
    fetchStores();
    fetchShopifyLocations();
  }, [fetchStores, fetchShopifyLocations]);
  
  const updateStore = (code, field, value) => {
    setStores(stores.map(s => 
      s.code === code ? { ...s, [field]: value } : s
    ));
  };
  
  const addTerminal = (storeCode) => {
    setStores(stores.map(s => {
      if (s.code === storeCode) {
        const terminals = s.terminals || [];
        return {
          ...s,
          terminals: [...terminals, { name: `Terminal ${terminals.length + 1}`, ip: '', port: '80' }]
        };
      }
      return s;
    }));
  };
  
  const updateTerminal = (storeCode, terminalIndex, field, value) => {
    setStores(stores.map(s => {
      if (s.code === storeCode) {
        const terminals = [...(s.terminals || [])];
        terminals[terminalIndex] = { ...terminals[terminalIndex], [field]: value };
        return { ...s, terminals };
      }
      return s;
    }));
  };
  
  const removeTerminal = (storeCode, terminalIndex) => {
    setStores(stores.map(s => {
      if (s.code === storeCode) {
        const terminals = (s.terminals || []).filter((_, i) => i !== terminalIndex);
        return { ...s, terminals };
      }
      return s;
    }));
  };
  
  const saveStore = async (store) => {
    setSaving({ ...saving, [store.code]: true });
    try {
      await axios.put(`${API_URL}/stores/${store.code}`, {
        name: store.name,
        address: store.address,
        phone: store.phone,
        smartpay_ip: store.smartpay_ip,
        smartpay_port: store.smartpay_port,
        terminals: store.terminals || [],
        shopify_location_id: store.shopify_location_id || null,
        open_time: store.open_time || null,
        close_time: store.close_time || null,
        gst_number: store.gst_number || null
      }, { headers: getAuthHeader() });
      
      toast.success(`${store.name} settings saved`);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving({ ...saving, [store.code]: false });
    }
  };
  
  const testSmartpay = async (store) => {
    if (!store.smartpay_ip) {
      toast.error('Please enter Smartpay IP address first');
      return;
    }
    
    toast.info(`Testing connection to ${store.smartpay_ip}:${store.smartpay_port || '80'}...`);
    
    try {
      const response = await axios.get(`${API_URL}/smartpay/test-connection`, { 
        headers: getAuthHeader(),
        params: {
          ip: store.smartpay_ip,
          port: store.smartpay_port || '80'
        }
      });
      
      if (response.data.connected) {
        toast.success(`EFTPOS terminal connected at ${store.name}`);
      } else {
        toast.warning('Could not connect to EFTPOS terminal');
      }
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
          Store Settings
        </h1>
        <p className="text-slate-500 mt-1">Configure your store locations and EFTPOS terminals</p>
      </div>
      
      {/* Store Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {stores.map(store => (
          <Card key={store.code} className="relative">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className={`w-5 h-5 ${store.code === 'orewa' ? 'text-blue-500' : 'text-emerald-500'}`} />
                {store.name}
              </CardTitle>
              <CardDescription>Store code: {store.code}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" /> Address
                </Label>
                <Input
                  value={store.address || ''}
                  onChange={(e) => updateStore(store.code, 'address', e.target.value)}
                  placeholder="Enter store address"
                  data-testid={`${store.code}-address`}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" /> Phone
                </Label>
                <Input
                  value={store.phone || ''}
                  onChange={(e) => updateStore(store.code, 'phone', e.target.value)}
                  placeholder="Enter phone number"
                  data-testid={`${store.code}-phone`}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-slate-400" /> GST number (printed on receipts)
                </Label>
                <Input
                  value={store.gst_number || ''}
                  onChange={(e) => updateStore(store.code, 'gst_number', e.target.value)}
                  placeholder="e.g. 134-201-606"
                  data-testid={`${store.code}-gst-number`}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" /> Trading hours (till open / close reminders)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Opens</p>
                    <Input
                      type="time"
                      value={store.open_time || (store.code === 'orewa' ? '09:30' : store.code === 'eastridge' ? '09:00' : '09:00')}
                      onChange={(e) => updateStore(store.code, 'open_time', e.target.value)}
                      data-testid={`${store.code}-open-time`}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Closes</p>
                    <Input
                      type="time"
                      value={store.close_time || (store.code === 'orewa' ? '17:00' : store.code === 'eastridge' ? '18:30' : '17:30')}
                      onChange={(e) => updateStore(store.code, 'close_time', e.target.value)}
                      data-testid={`${store.code}-close-time`}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-400">Staff are reminded to count the float from 30 min before opening and to close the till from 15 min before closing.</p>
              </div>
              
              {/* Shopify Location Linking */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-green-500" /> Shopify Location
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchShopifyLocations}
                    disabled={loadingLocations}
                    className="h-7"
                  >
                    {loadingLocations ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                
                <Select
                  value={store.shopify_location_id || 'none'}
                  onValueChange={(value) => updateStore(store.code, 'shopify_location_id', value === 'none' ? '' : value)}
                >
                  <SelectTrigger data-testid={`${store.code}-shopify-location`}>
                    <SelectValue placeholder="Select Shopify location to sync..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Not linked --</SelectItem>
                    {shopifyLocations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} {loc.city && `(${loc.city})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {store.shopify_location_id ? (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Linked to Shopify location ID: {store.shopify_location_id}
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠ Not linked - inventory won't sync to Shopify for this store
                  </p>
                )}
              </div>
              
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-emerald-500" /> EFTPOS Terminals
                  </h4>
                  <Badge variant="outline">
                    {(store.terminals || []).length} terminal{(store.terminals || []).length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                
                {/* Terminals List */}
                <div className="space-y-3">
                  {(store.terminals || []).map((terminal, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-sm space-y-2">
                      <div className="flex items-center justify-between">
                        <Input
                          value={terminal.name || `Terminal ${idx + 1}`}
                          onChange={(e) => updateTerminal(store.code, idx, 'name', e.target.value)}
                          className="w-40 h-8 text-sm font-medium"
                          data-testid={`${store.code}-terminal-${idx}-name`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTerminal(store.code, idx)}
                          className="text-red-500 hover:text-red-700 h-8"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">IP Address</Label>
                          <Input
                            value={terminal.ip || ''}
                            onChange={(e) => updateTerminal(store.code, idx, 'ip', e.target.value)}
                            placeholder="192.168.1.100"
                            className="h-8 text-sm"
                            data-testid={`${store.code}-terminal-${idx}-ip`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Port</Label>
                          <Input
                            value={terminal.port || '80'}
                            onChange={(e) => updateTerminal(store.code, idx, 'port', e.target.value)}
                            placeholder="80"
                            className="h-8 text-sm"
                            data-testid={`${store.code}-terminal-${idx}-port`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(store.terminals || []).length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-2">No terminals configured</p>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addTerminal(store.code)}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Terminal
                  </Button>
                </div>
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={() => saveStore(store)}
                  disabled={saving[store.code]}
                  className={`w-full ${store.code === 'orewa' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                  data-testid={`save-${store.code}`}
                >
                  {saving[store.code] ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save {store.name} Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default StoreSettings;
